import { Grid, Stack, Text } from '@mantine/core';

import { AsyncAutocomplete, EucdmCode, Select } from '@components';
import { useDeclarant, useGoodLocation } from '@hooks/declarant';
import { FieldRow, PolicyField, useFinalizeFieldRules } from '../policy';
import { useGlobalParameterCascades } from './useGlobalParameterCascades';

interface FinalizeGlobalParameterProps {
  disabled: boolean;
  brokerId: string;
}

const LOCATION_OF_GOODS = 'consignment.locationOfGoods.unLocode';
const OFFICE_PRESENTATION = 'consignment.customsOfficePresentation.referenceNumber';

export const FinalizeGlobalParameter: React.FC<FinalizeGlobalParameterProps> = ({ disabled, brokerId }) => {
  const { form, rule, fieldProps } = useFinalizeFieldRules();
  const { data: declarant } = useDeclarant();
  const { data: { data: goodsLocations } = {} } = useGoodLocation(declarant._id);

  const { customersNamesAndIds, customerValue, warehouseData, updateCustomerHandler, updateWarehouseHandler } = useGlobalParameterCascades({
    brokerId,
    goodsLocations,
  });

  return (
    <>
      <FieldRow mb="sm">
        <PolicyField {...fieldProps('messageInfo.declarationMainType')} span={6} />
        <PolicyField {...fieldProps('messageInfo.declarationType')} span={6} />
      </FieldRow>

      <FieldRow mb="sm">
        <PolicyField {...fieldProps('messageInfo.security')} span={4} />
        {rule('customer').present && (
          <Grid.Col span={4}>
            <Select
              placeholder="Select customer"
              label={rule('customer').label}
              data={customersNamesAndIds}
              value={customerValue}
              onChange={updateCustomerHandler}
              size="lg"
              disabled={rule('customer').disabled}
              data-pw={rule('customer').dataPw}
            />
          </Grid.Col>
        )}
        <PolicyField {...fieldProps('customsState.bcpMrn')} span={4} />
        <PolicyField {...fieldProps('messageInfo.presentationNotificationEstimatedDateTime')} span={4} />
      </FieldRow>

      <FieldRow mb="sm">
        <PolicyField {...fieldProps(OFFICE_PRESENTATION)} span={4} />
        {rule(LOCATION_OF_GOODS).present && (
          <Grid.Col span={4}>
            <EucdmCode path="declaration.consignment.locationOfGoods.unLocode">
              <AsyncAutocomplete
                {...form.getInputProps(LOCATION_OF_GOODS)}
                label={rule(LOCATION_OF_GOODS).label}
                data={goodsLocations.map((location) => ({ value: location.unLocode, label: location.unLocode }))}
                value={form.values.consignment.locationOfGoods.unLocode as string}
                size="lg"
                disabled={rule(LOCATION_OF_GOODS).disabled}
                data-pw={rule(LOCATION_OF_GOODS).dataPw}
              />
            </EucdmCode>
          </Grid.Col>
        )}
        {rule('warehouse').present && (
          <Grid.Col span={4}>
            <Stack>
              <Select
                key={form.values.warehouse as string}
                {...form.getInputProps('warehouse')}
                label={rule('warehouse').label}
                data={warehouseData}
                searchable
                size="lg"
                disabled={!form.values.consignment.locationOfGoods.unLocode || disabled}
                onChange={updateWarehouseHandler}
              />
              {form.values.warehouse && !warehouseData.some((w) => w.value === form.values.warehouse) && (
                <Text c="red" size="sm" mt={4}>
                  WARNING! The location set in documents is not in the list of bonded warehouses of location{' '}
                  {form.values.consignment.locationOfGoods.unLocode as string}
                </Text>
              )}
            </Stack>
          </Grid.Col>
        )}
      </FieldRow>

      <FieldRow mb="sm">
        <PolicyField {...fieldProps('consignment.customsOfficeExit.referenceNumber')} span={6} />
        <PolicyField {...fieldProps('consignment.customsOfficeExport.referenceNumber')} span={6} />
      </FieldRow>

      <FieldRow mb="sm">
        <PolicyField {...fieldProps('consignment.customsSupervisingOffice.referenceNumber')} span={12} />
      </FieldRow>
    </>
  );
};
